import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { AttributeDataType, type Attribute } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { attributeKeyFrom, emptyAttributeForm, toAttributeCreate } from '../api/attribute-mappers';
import { useCreateAttribute } from '../hooks/use-attribute-mutations';
import {
  attributeFormSchema,
  type AttributeFormInput,
  type AttributeFormValues,
} from '../schemas/attribute-form-schema';

import { AttributeFlagFields, AttributeUnitField } from './attribute-fields';

export interface AttributeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the saved attribute; the list opens it so values can be added. */
  onCreated: (attribute: Attribute) => void;
}

function DialogBody({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (attribute: Attribute) => void;
}) {
  const { t } = useTranslation(['attributes', 'common']);
  const message = useErrorMessage();
  const create = useCreateAttribute({
    successMessage: 'attributes:toasts.created',
    silentError: true,
  });
  const form = useForm<AttributeFormInput, unknown, AttributeFormValues>({
    resolver: useI18nResolver(attributeFormSchema),
    defaultValues: emptyAttributeForm(),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const dataType = form.watch('dataType');
  // The key follows the label until it is edited by hand.
  const keyTouched = useRef(false);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      const attribute = await create.mutateAsync(toAttributeCreate(values));
      onClose();
      onCreated(attribute);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert" className="sm:col-span-2">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('attributes:fields.label')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!keyTouched.current) form.setValue('key', attributeKeyFrom(e.target.value));
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('attributes:fields.key')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="font-mono text-[13px]"
                  onChange={(e) => {
                    keyTouched.current = true;
                    field.onChange(e);
                  }}
                />
              </FormControl>
              <FormDescription>{t('attributes:fields.keyHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="dataType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('attributes:fields.dataType')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(AttributeDataType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`attributes:dataType.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>{t(`attributes:dataTypeHint.${field.value}`)}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <AttributeUnitField dataType={dataType} />
        <div className="sm:col-span-2">
          <AttributeFlagFields dataType={dataType} />
        </div>
        <DialogFooter className="sm:col-span-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={create.isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? t('common:actions.saving') : t('attributes:create.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Create one attribute; editing happens on its own page, under the save bar. */
export function AttributeDialog({ open, onOpenChange, onCreated }: AttributeDialogProps) {
  const { t } = useTranslation('attributes');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{t('create.description')}</DialogDescription>
        </DialogHeader>
        {open ? <DialogBody onClose={() => onOpenChange(false)} onCreated={onCreated} /> : null}
      </DialogContent>
    </Dialog>
  );
}
